/**
 * 账号设置：查看手机 / 邮箱，修改密码。
 * 纯短信注册的用户没有密码 (has_password=false)，首次设置时不需要原密码。
 * 改密成功后退出登录，用新密码重新登录。
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserRound } from "lucide-react";
import { Alert, Button, Card, CardContent, CardHeader, Field, Input, PageHeader } from "@/components/ui";
import { ApiError } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth-store";
import { useChangePassword, useCurrentUser, useLogout } from "@/features/auth/hooks";

const ROLE_TEXT: Record<string, string> = {
  super_admin: "超级管理员",
  company_admin: "公司管理员",
  user: "普通用户",
};

export function AccountPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const me = useCurrentUser();
  const changeMut = useChangePassword();
  const logoutMut = useLogout();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // /api/me 没返回前默认需要原密码，避免误把有密码的用户当成短信用户
  const hasPassword = me.data?.has_password ?? true;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (hasPassword && !oldPwd) { setErr("请输入原密码"); return; }
    if (newPwd.length < 6) { setErr("新密码至少 6 位"); return; }
    if (newPwd !== newPwd2) { setErr("两次输入的新密码不一致"); return; }
    try {
      await changeMut.mutateAsync({ old_password: hasPassword ? oldPwd : "", new_password: newPwd });
      setDone(true);
      // 稍作停留让用户看到提示，再退出重新登录
      setTimeout(async () => {
        await logoutMut.mutateAsync().catch(() => undefined);
        navigate("/login", { replace: true });
      }, 1200);
    } catch (e2) {
      setErr(e2 instanceof ApiError
        ? ((e2.body as { detail?: string } | null)?.detail || `修改失败 (${e2.status})`)
        : "修改失败");
    }
  };

  const info = me.data;
  const rows: Array<{ label: string; value?: string | null; mono?: boolean }> = [
    { label: "用户名", value: info?.username ?? user?.username, mono: true },
    { label: "显示名", value: info?.display_name ?? user?.display_name },
    { label: "角色", value: ROLE_TEXT[info?.role ?? user?.role ?? ""] ?? info?.role ?? user?.role },
    { label: "所属公司", value: info?.company_name ?? user?.company_name },
    { label: "手机号", value: info?.phone ?? user?.phone },
    { label: "邮箱", value: info?.email ?? user?.email },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 md:py-8">
      <PageHeader title="账号设置" description="查看账号信息，修改登录密码。" />

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2 font-semibold"><UserRound aria-hidden className="h-4 w-4 text-muted-foreground" />基本信息</span>}
          description="手机号、邮箱如需修改请联系管理员。"
        />
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            {rows.map((r) => (
              <div key={r.label} className="min-w-0 space-y-0.5">
                <dt className="text-caption text-muted-foreground">{r.label}</dt>
                <dd className={r.mono ? "truncate font-mono text-sm" : "truncate text-sm font-medium"}>{r.value || "-"}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2 font-semibold"><KeyRound aria-hidden className="h-4 w-4 text-muted-foreground" />{hasPassword ? "修改密码" : "设置密码"}</span>}
          description={
            hasPassword
              ? "修改成功后需要用新密码重新登录。"
              : "你当前通过短信验证码登录，尚未设置密码。设置后也可用用户名 + 密码登录。"
          }
        />
        <CardContent>
          {done ? (
            <Alert variant="success">密码已更新，请使用新密码重新登录…</Alert>
          ) : (
            <form onSubmit={submit} className="max-w-sm space-y-4" noValidate>
              {hasPassword && (
                <Field label="原密码">
                  <Input type="password" autoComplete="current-password" value={oldPwd}
                    onChange={(e) => setOldPwd(e.target.value)} />
                </Field>
              )}
              <Field label="新密码" hint="至少 6 位">
                <Input type="password" autoComplete="new-password" value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)} />
              </Field>
              <Field label="确认新密码">
                <Input type="password" autoComplete="new-password" value={newPwd2}
                  onChange={(e) => setNewPwd2(e.target.value)} />
              </Field>
              {err && <Alert>{err}</Alert>}
              <Button type="submit" loading={changeMut.isPending}>
                {changeMut.isPending ? "提交中…" : hasPassword ? "修改密码" : "设置密码"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
